/**
 * App Store - Global application state
 * Using Zustand with TypeScript and persist middleware
 * Following Context7 best practices
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface AppState {
  // State
  theme: Theme;
  sidebarOpen: boolean;
  isLoading: boolean;
  /** Override the user's actual role for switching between FiadoSA / UNE-Credito views */
  activeRole: string | null;

  // Actions
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setLoading: (loading: boolean) => void;
  setActiveRole: (role: string | null) => void;
}

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        theme: 'system',
        sidebarOpen: true,
        isLoading: false,
        activeRole: null,

        // Actions
        setTheme: (theme) => set({ theme }, false, 'setTheme'),

        toggleSidebar: () =>
          set((state) => ({ sidebarOpen: !state.sidebarOpen }), false, 'toggleSidebar'),

        setSidebarOpen: (open) => set({ sidebarOpen: open }, false, 'setSidebarOpen'),

        setLoading: (loading) => set({ isLoading: loading }, false, 'setLoading'),

        setActiveRole: (role) => set({ activeRole: role }, false, 'setActiveRole'),
      }),
      {
        name: 'app-storage',
        partialize: (state) => ({
          theme: state.theme,
          sidebarOpen: state.sidebarOpen,
          activeRole: state.activeRole,
        }),
      }
    ),
    { name: 'AppStore' }
  )
);
